import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AssignRoleResponse,
  GroupRole,
  GroupRoleAssignedEnvelope,
  GroupRoleUnassignedEnvelope,
  UnassignRoleResponse,
  assignRole as universalAssignRole,
  unassignRole as universalUnassignRole,
} from './social-groups-v2-group-role-roles.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/groups' };

export function assignRole(httpClient: HttpClient): AssignRoleSignature {
  return (groupId: string, memberIds: string[], role: GroupRole) =>
    universalAssignRole(
      groupId,
      memberIds,
      role,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignRoleSignature {
  /**
   * Assigns a specific role to group members.
   *
   * Calling this method overrides the group member's current `role.value`.
   *
   * >**Notes:**
   * > + Only group admins can assign roles.
   * > + You cannot create new members with this method.
   * @param - Group ID.
   * @param - Member IDs. Limited to 100 member IDs. See the Members API for details.
   * @param - Role to assign.
   */
  (groupId: string, memberIds: string[], role: GroupRole): Promise<
    NonNullablePaths<
      AssignRoleResponse,
      `groupId` | `memberIds` | `role.value`,
      3
    >
  >;
}

export function unassignRole(httpClient: HttpClient): UnassignRoleSignature {
  return (groupId: string, memberIds: string[], role: GroupRole) =>
    universalUnassignRole(
      groupId,
      memberIds,
      role,
      // @ts-ignore
      { httpClient }
    );
}

interface UnassignRoleSignature {
  /**
   * Unassigns a role from group members.
   *
   * You can only unassign `ADMIN` roles. Calling this method with group members
   * with `role.value` set to `MEMBER` returns an error.
   *
   * > **Notes:**
   * > + Only group admins can assign roles.
   * > + You cannot remove members with this method.
   * @param - Group ID.
   * @param - Member IDs. Limited to 100 member IDs. See the Members API for details.
   * @param - Role to unassign.
   */
  (groupId: string, memberIds: string[], role: GroupRole): Promise<
    NonNullablePaths<
      UnassignRoleResponse,
      `groupId` | `siteMemberIds` | `memberIds` | `role.value`,
      3
    >
  >;
}

export const onGroupRoleAssigned = EventDefinition(
  'wix.social_groups.v2.group_role_assigned',
  true,
  (event: GroupRoleAssignedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.groupMember.joinedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<GroupRoleAssignedEnvelope>();
export const onGroupRoleUnassigned = EventDefinition(
  'wix.social_groups.v2.group_role_unassigned',
  true,
  (event: GroupRoleUnassignedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.groupMember.joinedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<GroupRoleUnassignedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AssignRoleRequest,
  AssignRoleResponse,
  BaseEventMetadata,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GroupMember,
  GroupRole,
  GroupRoleAssignedEnvelope,
  GroupRoleUnassignedEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  RestoreInfo,
  Role,
  RoleAssignedToGroupMember,
  RoleUnassignedFromGroupMember,
  UnassignRoleRequest,
  UnassignRoleResponse,
  WebhookIdentityType,
} from './social-groups-v2-group-role-roles.universal.js';
